// Mindfulness Mode Types
export type FocusModeType = 'collaboration' | 'focus' | 'deep-work';

export interface FocusModeConfig {
  mode: FocusModeType;
  selectedChannelIds?: string[]; // For focus mode - up to 3 channels
  activatedAt?: string;
  expiresAt?: string; // Optional auto-expire
}

export interface DeepWorkQuote {
  text: string;
  author: string;
  imageUrl?: string;
}

export interface UrgentMessageRequest {
  messageId: string;
  senderId: string;
  senderName: string;
  senderAvatarUrl?: string;
  content: string;
  channelId: string;
  channelName?: string;
  channelType?: 'Channel' | 'DirectMessage';
  timestamp: string;
}

// Focus mode metadata
export const FOCUS_MODES: Record<FocusModeType, {
  name: string;
  description: string;
  icon: string;
  color: string;
  ringColor: string;
  gradient: string;
}> = {
  'collaboration': {
    name: 'Collaboration',
    description: 'Full access to all features and channels',
    icon: '🤝',
    color: 'green',
    ringColor: 'ring-green-500',
    gradient: 'from-green-500 to-emerald-500',
  },
  'focus': {
    name: 'Focus',
    description: 'Limited to selected channels only',
    icon: '🎯',
    color: 'amber',
    ringColor: 'ring-amber-500',
    gradient: 'from-amber-500 to-orange-500',
  },
  'deep-work': {
    name: 'Deep Work',
    description: 'Minimal distractions, urgent mentions only',
    icon: '🧘',
    color: 'purple',
    ringColor: 'ring-purple-500',
    gradient: 'from-purple-500 to-indigo-500',
  },
};

// Curated deep work quotes
export const DEEP_WORK_QUOTES: DeepWorkQuote[] = [
  {
    text: "The ability to perform deep work is becoming increasingly rare at exactly the same time it is becoming increasingly valuable in our economy.",
    author: "Cal Newport",
  },
  {
    text: "Focus is a matter of deciding what things you're not going to do.",
    author: "John Carmack",
  },
  {
    text: "Almost everything will work again if you unplug it for a few minutes, including you.",
    author: "Anne Lamott",
  },
  {
    text: "The successful warrior is the average person, with laser-like focus.",
    author: "Bruce Lee",
  },
  {
    text: "Concentrate all your thoughts upon the work at hand. The sun's rays do not burn until brought to a focus.",
    author: "Alexander Graham Bell",
  },
  {
    text: "Your work is to discover your work and then with all your heart to give yourself to it.",
    author: "Buddha",
  },
  {
    text: "The mind is everything. What you think you become.",
    author: "Buddha",
  },
  {
    text: "In the midst of chaos, there is also opportunity.",
    author: "Sun Tzu",
  },
  {
    text: "Do not dwell in the past, do not dream of the future, concentrate the mind on the present moment.",
    author: "Buddha",
  },
  {
    text: "The quieter you become, the more you can hear.",
    author: "Ram Dass",
  },
  {
    text: "Where focus goes, energy flows.",
    author: "Tony Robbins",
  },
  {
    text: "Productivity is never an accident. It is always the result of a commitment to excellence, intelligent planning, and focused effort.",
    author: "Paul J. Meyer",
  },
  {
    text: "It is not enough to be busy; so are the ants. The question is: What are we busy about?",
    author: "Henry David Thoreau",
  },
  {
    text: "The secret of change is to focus all of your energy not on fighting the old, but on building the new.",
    author: "Socrates",
  },
  {
    text: "The more you focus, the more you finish.",
    author: "Unknown",
  },
  {
    text: "To do two things at once is to do neither.",
    author: "Publilius Syrus",
  },
  {
    text: "It is not that I'm so smart. But I stay with the questions much longer.",
    author: "Albert Einstein",
  },
  {
    text: "Your mind will answer most questions if you learn to relax and wait for the answer.",
    author: "William S. Burroughs",
  },
  {
    text: "Work deeply; the world is full of shallow distractions.",
    author: "Unknown",
  },
  {
    text: "Be where your feet are.",
    author: "Unknown",
  },
  {
    text: "A little progress each day adds up to big results.",
    author: "Unknown",
  },
  {
    text: "What you stay focused on will grow.",
    author: "Roy T. Bennett",
  },
  {
    text: "Simplicity is the ultimate sophistication.",
    author: "Leonardo da Vinci",
  },
  {
    text: "Beware the barrenness of a busy life.",
    author: "Socrates",
  },
  {
    text: "The calm mind allows one to connect with the inner self.",
    author: "Unknown",
  },
  {
    text: "The future depends on what you do today.",
    author: "Mahatma Gandhi",
  },
  {
    text: "You can do anything, but not everything.",
    author: "David Allen",
  },
  {
    text: "An hour of focus is worth a day of effort.",
    author: "Unknown",
  },
  {
    text: "Do one thing every day that scares you.",
    author: "Eleanor Roosevelt",
  },
  {
    text: "Clarity about what matters provides clarity about what does not.",
    author: "Cal Newport",
  },
  {
    text: "The soul always knows what to do to heal itself. The challenge is to silence the mind.",
    author: "Caroline Myss",
  },
  {
    text: "It is better to do one task well than many poorly.",
    author: "Unknown",
  },
  {
    text: "Attention is the rarest and purest form of generosity.",
    author: "Simone Weil",
  },
  {
    text: "Stillness is where creativity and solutions are found.",
    author: "Unknown",
  },
  {
    text: "One day or day one. You decide.",
    author: "Unknown",
  },
  // Additional quotes for variety
  {
    text: "The greatest weapon against stress is our ability to choose one thought over another.",
    author: "William James",
  },
  {
    text: "Don't count the days, make the days count.",
    author: "Muhammad Ali",
  },
  {
    text: "Starve your distractions, feed your focus.",
    author: "Unknown",
  },
  {
    text: "Great things are done by a series of small things brought together.",
    author: "Vincent van Gogh",
  },
  {
    text: "The only way to do great work is to love what you do.",
    author: "Steve Jobs",
  },
  {
    text: "Small deeds done are better than great deeds planned.",
    author: "Peter Marshall",
  },
  {
    text: "The key is not to prioritize what's on your schedule, but to schedule your priorities.",
    author: "Stephen Covey",
  },
  {
    text: "We are what we repeatedly do. Excellence, then, is not an act, but a habit.",
    author: "Aristotle",
  },
  {
    text: "Lost time is never found again.",
    author: "Benjamin Franklin",
  },
  {
    text: "Action is the foundational key to all success.",
    author: "Pablo Picasso",
  },
  {
    text: "Either you run the day or the day runs you.",
    author: "Jim Rohn",
  },
  {
    text: "Start where you are. Use what you have. Do what you can.",
    author: "Arthur Ashe",
  },
  {
    text: "The journey of a thousand miles begins with one step.",
    author: "Lao Tzu",
  },
  {
    text: "If you want to lift yourself up, lift up someone else.",
    author: "Booker T. Washington",
  },
  {
    text: "Life is what happens when you're busy making other plans.",
    author: "John Lennon",
  },
  {
    text: "Don't watch the clock; do what it does. Keep going.",
    author: "Sam Levenson",
  },
  {
    text: "Nothing is impossible, the word itself says 'I'm possible'!",
    author: "Audrey Hepburn",
  },
  {
    text: "Setting goals is the first step in turning the invisible into the visible.",
    author: "Tony Robbins",
  },
  {
    text: "Absorb what is useful, discard what is useless and add what is specifically your own.",
    author: "Bruce Lee",
  },
  {
    text: "In the middle of every difficulty lies opportunity.",
    author: "Albert Einstein",
  },
  {
    text: "Quality is not an act, it is a habit.",
    author: "Aristotle",
  },
  {
    text: "Believe you can and you're halfway there.",
    author: "Theodore Roosevelt",
  },
  {
    text: "The best time to plant a tree was 20 years ago. The second best time is now.",
    author: "Chinese Proverb",
  },
  {
    text: "Your limitation—it's only your imagination.",
    author: "Unknown",
  },
  {
    text: "Push yourself, because no one else is going to do it for you.",
    author: "Unknown",
  },
  {
    text: "Sometimes later becomes never. Do it now.",
    author: "Unknown",
  },
  {
    text: "Great things never come from comfort zones.",
    author: "Unknown",
  },
  {
    text: "Dream it. Wish it. Do it.",
    author: "Unknown",
  },
  {
    text: "Success doesn't just find you. You have to go out and get it.",
    author: "Unknown",
  },
  {
    text: "The harder you work for something, the greater you'll feel when you achieve it.",
    author: "Unknown",
  },
  {
    text: "Dream bigger. Do bigger.",
    author: "Unknown",
  },
  {
    text: "Don't stop when you're tired. Stop when you're done.",
    author: "Unknown",
  },
  {
    text: "Wake up with determination. Go to bed with satisfaction.",
    author: "Unknown",
  },
  {
    text: "Little things make big days.",
    author: "Unknown",
  },
  {
    text: "It's going to be hard, but hard does not mean impossible.",
    author: "Unknown",
  },
  {
    text: "Don't wait for opportunity. Create it.",
    author: "Unknown",
  },
  {
    text: "The way to get started is to quit talking and begin doing.",
    author: "Walt Disney",
  },
  {
    text: "The pessimist sees difficulty in every opportunity. The optimist sees opportunity in every difficulty.",
    author: "Winston Churchill",
  },
  {
    text: "It always seems impossible until it's done.",
    author: "Nelson Mandela",
  },
  {
    text: "If you are working on something that you really care about, you don't have to be pushed. The vision pulls you.",
    author: "Steve Jobs",
  },
  {
    text: "People who are crazy enough to think they can change the world, are the ones who do.",
    author: "Rob Siltanen",
  },
  {
    text: "Failure will never overtake me if my determination to succeed is strong enough.",
    author: "Og Mandino",
  },
  {
    text: "Knowing is not enough; we must apply. Wishing is not enough; we must do.",
    author: "Johann Wolfgang von Goethe",
  },
  {
    text: "Imagine your life is perfect in every respect; what would it look like?",
    author: "Brian Tracy",
  },
  {
    text: "We may encounter many defeats but we must not be defeated.",
    author: "Maya Angelou",
  },
  {
    text: "Whether you think you can or think you can't, you're right.",
    author: "Henry Ford",
  },
  {
    text: "Security is mostly a superstition. Life is either a daring adventure or nothing.",
    author: "Helen Keller",
  },
  {
    text: "The only limit to our realization of tomorrow will be our doubts of today.",
    author: "Franklin D. Roosevelt",
  },
  {
    text: "Creativity is intelligence having fun.",
    author: "Albert Einstein",
  },
  {
    text: "What you get by achieving your goals is not as important as what you become by achieving your goals.",
    author: "Zig Ziglar",
  },
  {
    text: "I have not failed. I've just found 10,000 ways that won't work.",
    author: "Thomas Edison",
  },
  {
    text: "A person who never made a mistake never tried anything new.",
    author: "Albert Einstein",
  },
  {
    text: "The mind is not a vessel to be filled, but a fire to be kindled.",
    author: "Plutarch",
  },
  {
    text: "What lies behind us and what lies before us are tiny matters compared to what lies within us.",
    author: "Ralph Waldo Emerson",
  },
  {
    text: "Learn as if you will live forever, live like you will die tomorrow.",
    author: "Mahatma Gandhi",
  },
  {
    text: "When you change your thoughts, remember to also change your world.",
    author: "Norman Vincent Peale",
  },
  {
    text: "It is never too late to be what you might have been.",
    author: "George Eliot",
  },
  {
    text: "The only person you are destined to become is the person you decide to be.",
    author: "Ralph Waldo Emerson",
  },
  {
    text: "Everything you've ever wanted is on the other side of fear.",
    author: "George Addair",
  },
  {
    text: "Success usually comes to those who are too busy to be looking for it.",
    author: "Henry David Thoreau",
  },
  {
    text: "If you really look closely, most overnight successes took a long time.",
    author: "Steve Jobs",
  },
  {
    text: "The secret of getting ahead is getting started.",
    author: "Mark Twain",
  },
  {
    text: "Don't be afraid to give up the good to go for the great.",
    author: "John D. Rockefeller",
  },
  {
    text: "I find that the harder I work, the more luck I seem to have.",
    author: "Thomas Jefferson",
  },
  {
    text: "Success is not final; failure is not fatal: It is the courage to continue that counts.",
    author: "Winston Churchill",
  },
  {
    text: "Never give up on a dream just because of the time it will take to accomplish it. The time will pass anyway.",
    author: "Earl Nightingale",
  },
  {
    text: "If you set your goals ridiculously high and it's a failure, you will fail above everyone else's success.",
    author: "James Cameron",
  },
  {
    text: "You don't have to be great to start, but you have to start to be great.",
    author: "Zig Ziglar",
  },
  {
    text: "A year from now you may wish you had started today.",
    author: "Karen Lamb",
  },
  {
    text: "The most difficult thing is the decision to act, the rest is merely tenacity.",
    author: "Amelia Earhart",
  },
  {
    text: "Every strike brings me closer to the next home run.",
    author: "Babe Ruth",
  },
  {
    text: "Definiteness of purpose is the starting point of all achievement.",
    author: "W. Clement Stone",
  },
  {
    text: "Life isn't about getting and having, it's about giving and being.",
    author: "Kevin Kruse",
  },
  {
    text: "Life is 10% what happens to me and 90% of how I react to it.",
    author: "Charles Swindoll",
  },
  {
    text: "The most common way people give up their power is by thinking they don't have any.",
    author: "Alice Walker",
  },
  {
    text: "The mind is its own place, and in itself can make a heaven of hell, a hell of heaven.",
    author: "John Milton",
  },
  {
    text: "The best revenge is massive success.",
    author: "Frank Sinatra",
  },
  {
    text: "I alone cannot change the world, but I can cast a stone across the water to create many ripples.",
    author: "Mother Teresa",
  },
  {
    text: "Change your thoughts and you change your world.",
    author: "Norman Vincent Peale",
  },
  {
    text: "If you hear a voice within you say 'you cannot paint,' then by all means paint and that voice will be silenced.",
    author: "Vincent Van Gogh",
  },
  {
    text: "There is only one way to avoid criticism: do nothing, say nothing, and be nothing.",
    author: "Aristotle",
  },
  {
    text: "Ask and it will be given to you; search, and you will find; knock and the door will be opened for you.",
    author: "Jesus",
  },
  {
    text: "The only way to achieve the impossible is to believe it is possible.",
    author: "Charles Kingsleigh",
  },
  {
    text: "What we fear doing most is usually what we most need to do.",
    author: "Tim Ferriss",
  },
  {
    text: "How wonderful it is that nobody need wait a single moment before starting to improve the world.",
    author: "Anne Frank",
  },
  {
    text: "When I let go of what I am, I become what I might be.",
    author: "Lao Tzu",
  },
  {
    text: "Life is not measured by the number of breaths we take, but by the moments that take our breath away.",
    author: "Maya Angelou",
  },
  {
    text: "Happiness is not something readymade. It comes from your own actions.",
    author: "Dalai Lama",
  },
  {
    text: "If you want to achieve greatness stop asking for permission.",
    author: "Unknown",
  },
  {
    text: "Things work out best for those who make the best of how things work out.",
    author: "John Wooden",
  },
  {
    text: "To live a creative life, we must lose our fear of being wrong.",
    author: "Joseph Chilton Pearce",
  },
  {
    text: "If you are not willing to risk the usual you will have to settle for the ordinary.",
    author: "Jim Rohn",
  },
  {
    text: "Trust because you are willing to accept the risk, not because it's safe or certain.",
    author: "Unknown",
  },
  {
    text: "Take up one idea. Make that one idea your life—think of it, dream of it, live on that idea.",
    author: "Swami Vivekananda",
  },
  {
    text: "All our dreams can come true if we have the courage to pursue them.",
    author: "Walt Disney",
  },
  {
    text: "Good things come to people who wait, but better things come to those who go out and get them.",
    author: "Unknown",
  },
  {
    text: "If you do what you always did, you will get what you always got.",
    author: "Unknown",
  },
  {
    text: "Successful people do what unsuccessful people are not willing to do. Don't wish it were easier; wish you were better.",
    author: "Jim Rohn",
  },
];
